// API client for the tour-builder. It talks to TWO API-key-protected REST APIs:
//
//   ingest API (API_BASE / API_KEY) — the artifact data:
//     - listArtifacts()            GET /artifacts?status=all   (the "full scan")
//     - getArtifact(id)            GET /artifacts/{id}
//     - updateArtifact(id, patch)  PUT /artifacts/{id}         (we send { tourTags })
//
//   tours API (TOURS_API_BASE / TOURS_API_KEY) — the durable TOUR#<slug> order records:
//     - listTours()                GET /tours
//     - createTour(name)           POST /tours        (mints the record; name=slug)
//     - putTour(slug, patch)       PUT /tours/{slug}  (we send { order })
//
// Each call carries the matching `x-api-key` header.

import { API_BASE, API_KEY, TOURS_API_BASE, TOURS_API_KEY } from "./config";
import { getIdToken, notifyAuthExpired } from "./auth";
import type {
  Artifact,
  ListArtifactsResponse,
  ListStatusQuery,
  UpdateArtifactRequest,
  ApiErrorBody,
  Tour,
  ListToursResponse,
  UpdateTourRequest,
  ExportTourResponse,
} from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly body: string;
  constructor(status: number, message: string, body: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function parseError(res: Response): Promise<never> {
  const text = await res.text().catch(() => "");
  let message = `${res.status} ${res.statusText}`;
  if (text) {
    try {
      const j = JSON.parse(text) as Partial<ApiErrorBody>;
      if (j && typeof j.error === "string") message = j.error;
    } catch {
      // non-JSON body; keep the status line
    }
  }
  throw new ApiError(res.status, message, text);
}

/** Core request helper (adds x-api-key + Cognito token, parses JSON, throws ApiError on !ok). */
async function coreRequest<T>(
  base: string,
  key: string,
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, headers, ...rest } = init ?? {};
  // One ID token works for BOTH APIs (same shared pool, no aud check).
  const token = await getIdToken();
  const res = await fetch(`${base}${path}`, {
    ...rest,
    headers: {
      "x-api-key": key,
      ...(token ? { Authorization: token } : {}),
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(headers as Record<string, string> | undefined),
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (!res.ok) {
    // 401 = the Cognito authorizer rejected the token (missing/expired/refresh
    // failed). Drop the session and re-show login. (403 = bad API key / access
    // denied — a build/config issue, NOT session expiry, so don't sign out on it.)
    if (res.status === 401) notifyAuthExpired();
    return parseError(res);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

const request = <T>(path: string, init?: RequestInit & { json?: unknown }) =>
  coreRequest<T>(API_BASE, API_KEY, path, init);

const toursRequest = <T>(path: string, init?: RequestInit & { json?: unknown }) =>
  coreRequest<T>(TOURS_API_BASE, TOURS_API_KEY, path, init);

// ---------------------------------------------------------------------------
// Ingest API — artifacts
// ---------------------------------------------------------------------------

/**
 * GET /artifacts?status= -> { items }. status defaults to "all". This is the
 * "full scan" the tour-builder groups by `tourTags` to derive tours.
 */
export async function listArtifacts(
  status: ListStatusQuery = "all",
): Promise<Artifact[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const r = await request<ListArtifactsResponse>(`/artifacts${qs}`);
  return r.items ?? [];
}

/** GET /artifacts/{id} -> Artifact (404 -> ApiError with status 404). */
export function getArtifact(id: string): Promise<Artifact> {
  return request<Artifact>(`/artifacts/${encodeURIComponent(id)}`);
}

/**
 * PUT /artifacts/{id} -> Artifact. The tour-builder sends { tourTags: string[] }
 * (the FULL new array — the server REPLACES the field, sets fieldSources.tourTags
 * =manual, and recomputes searchBlob/updatedAt). Adding/removing tour membership
 * is the only durable write this app makes to artifacts.
 */
export function updateArtifact(
  id: string,
  patch: UpdateArtifactRequest,
): Promise<Artifact> {
  return request<Artifact>(`/artifacts/${encodeURIComponent(id)}`, {
    method: "PUT",
    json: patch,
  });
}

// ---------------------------------------------------------------------------
// Tours API — durable TOUR#<slug> order records
// ---------------------------------------------------------------------------

/** GET /tours -> Tour[] (every TOUR#<slug> record's slug + order). */
export async function listTours(): Promise<Tour[]> {
  const r = await toursRequest<ListToursResponse>(`/tours`);
  return r.tours ?? [];
}

/**
 * POST /tours { name } -> Tour. Used to MINT a TOUR#<slug> record the first time
 * a tour's order is persisted. We pass the tag slug AS the name: the server
 * slugifies it, and canonical tag slugs round-trip (slugify(slug) === slug), so
 * the minted record's slug equals the tag. The caller verifies and falls back if
 * not.
 */
export function createTour(name: string): Promise<Tour> {
  return toursRequest<Tour>(`/tours`, { method: "POST", json: { name } });
}

/**
 * PUT /tours/{slug} -> Tour. Persists the durable arrangement: an arbitrary patch
 * of the tour record (the tour-builder sends `order` and/or `quotes`). Absent keys
 * are left untouched server-side; `quotes: []` explicitly clears them.
 */
export function putTour(slug: string, patch: UpdateTourRequest): Promise<Tour> {
  return toursRequest<Tour>(`/tours/${encodeURIComponent(slug)}`, {
    method: "PUT",
    json: patch,
  });
}

/**
 * POST /tours/{slug}/export -> { url, slug, slides? }. SYNCHRONOUS: the server
 * (re)generates the tour's self-contained OFFLINE HTML (downloading + downscaling
 * images), then returns its same-origin attachment URL. Can take ~5–25s. No body.
 */
export function exportTour(slug: string): Promise<ExportTourResponse> {
  return toursRequest<ExportTourResponse>(
    `/tours/${encodeURIComponent(slug)}/export`,
    { method: "POST" },
  );
}
